const LEASES_PATH = '/api/v1/holo/runtime-leases'

function requiredString(value, name) {
  if (typeof value !== 'string' || !value.trim())
    throw new TypeError(`${name} must be a non-empty string`)
  return value.trim()
}

function requireGeneration(value) {
  if (!Number.isSafeInteger(value) || value <= 0)
    throw new TypeError('generation must be a positive safe integer')
  return value
}

function normalizedBaseUrl(value) {
  const url = new URL(requiredString(value, 'baseUrl'))
  url.pathname = '/'
  url.search = ''
  url.hash = ''
  return url.toString().replace(/\/$/, '')
}

function parseLease(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('invalid_runtime_lease')
  const lease = {
    runtime_lease_id: requiredString(value.runtime_lease_id, 'runtime_lease.runtime_lease_id'),
    user_id: requiredString(value.user_id, 'runtime_lease.user_id'),
    character_id: requiredString(value.character_id, 'runtime_lease.character_id'),
    client_runtime_id: requiredString(value.client_runtime_id, 'runtime_lease.client_runtime_id'),
    generation: requireGeneration(value.generation),
    opened_at: Number(value.opened_at),
    renewed_at: Number(value.renewed_at),
    expires_at: Number(value.expires_at),
  }
  for (const field of ['opened_at', 'renewed_at', 'expires_at']) {
    if (!Number.isFinite(lease[field]))
      throw new Error(`runtime_lease.${field} must be finite`)
  }
  return Object.freeze(lease)
}

function parseActivation(value, { allowNullLease = false, allowDifferentCurrentCharacter = false } = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('invalid_activation_response')
  const characterId = requiredString(value.character_id, 'character_id')
  if (value.runtime_lease == null) {
    if (!allowNullLease)
      throw new Error('runtime_lease_missing')
    return Object.freeze({ character_id: characterId, runtime_lease: null })
  }
  const lease = parseLease(value.runtime_lease)
  if (!allowDifferentCurrentCharacter && lease.character_id !== characterId)
    throw new Error('runtime_lease_character_mismatch')
  return Object.freeze({ character_id: characterId, runtime_lease: lease })
}

export class HoloRuntimeLeaseError extends Error {
  constructor(message, { status, code } = {}) {
    super(message)
    this.name = 'HoloRuntimeLeaseError'
    this.status = status
    this.code = code
  }
}

async function errorFromResponse(response) {
  let code = ''
  try {
    const body = await response.json()
    if (body && typeof body.error === 'string')
      code = body.error
  }
  catch {}
  return new HoloRuntimeLeaseError(
    code || `runtime_lease_http_${response.status}`,
    { status: response.status, code: code || undefined },
  )
}

export function createHoloRuntimeLeaseClient({
  baseUrl,
  fetchImpl = globalThis.fetch,
} = {}) {
  const origin = normalizedBaseUrl(baseUrl)
  if (typeof fetchImpl !== 'function')
    throw new TypeError('fetchImpl must be a function')

  const url = path => new URL(path, origin).toString()

  async function request(path, init, parseOptions) {
    const response = await fetchImpl(url(path), {
      ...init,
      headers: {
        accept: 'application/json',
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    })
    if (!response.ok)
      throw await errorFromResponse(response)
    if (response.status === 204) {
      if (init?.method !== 'DELETE')
        throw new Error('runtime_lease_missing')
      return null
    }
    return parseActivation(await response.json(), parseOptions)
  }

  return Object.freeze({
    async current(characterId) {
      const id = requiredString(characterId, 'characterId')
      return request(
        `${LEASES_PATH}/current?character_id=${encodeURIComponent(id)}`,
        { method: 'GET' },
        { allowNullLease: true, allowDifferentCurrentCharacter: true },
      )
    },

    async acquire({ characterId, clientRuntimeId } = {}) {
      return request(LEASES_PATH, {
        method: 'POST',
        body: JSON.stringify({
          character_id: requiredString(characterId, 'characterId'),
          client_runtime_id: requiredString(clientRuntimeId, 'clientRuntimeId'),
        }),
      })
    },

    async renew({ runtimeLeaseId, clientRuntimeId, generation } = {}) {
      return request(`${LEASES_PATH}/${encodeURIComponent(requiredString(runtimeLeaseId, 'runtimeLeaseId'))}/renew`, {
        method: 'POST',
        body: JSON.stringify({
          client_runtime_id: requiredString(clientRuntimeId, 'clientRuntimeId'),
          generation: requireGeneration(generation),
        }),
      })
    },

    async takeover({ expectedRuntimeLeaseId, characterId, clientRuntimeId } = {}) {
      return request(`${LEASES_PATH}/${encodeURIComponent(requiredString(expectedRuntimeLeaseId, 'expectedRuntimeLeaseId'))}/takeover`, {
        method: 'POST',
        body: JSON.stringify({
          character_id: requiredString(characterId, 'characterId'),
          client_runtime_id: requiredString(clientRuntimeId, 'clientRuntimeId'),
        }),
      })
    },

    async release({ runtimeLeaseId, clientRuntimeId, generation, reason = 'released' } = {}) {
      await request(`${LEASES_PATH}/${encodeURIComponent(requiredString(runtimeLeaseId, 'runtimeLeaseId'))}`, {
        method: 'DELETE',
        body: JSON.stringify({
          client_runtime_id: requiredString(clientRuntimeId, 'clientRuntimeId'),
          generation: requireGeneration(generation),
          reason: requiredString(reason, 'reason'),
        }),
      })
      return true
    },
  })
}
